import { createContext, useContext, useEffect, useState, useRef } from "react";

import { getFirestore, doc, getDoc, updateDoc, onSnapshot, collection, query, orderBy, limit } from "firebase/firestore";

import { useAuth } from "./useAuth.jsx";

//Create Context
const DatabaseReaderContext = createContext();

//Context Provider
const DatabaseReaderContextProvider = ({ children }) => {
  //HOOKS
  const { authContextLoaded, user, tooManyDevices } = useAuth();

  //STATE
  const [userRegion, setUserRegion] = useState();
  const [warehouses, setWarehouses] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [preferredCurrency, setPreferredCurrency] = useState("$ CAD");

  useEffect(() => {
    if (!authContextLoaded || user == null) {
      return;
    }

    (async () => {
      const userDoc = await getDoc(doc(getFirestore(), "users", user.uid));
      const region = userDoc.data().region;
      setUserRegion(userDoc.data().region);
      setPreferredCurrency(userDoc.data().preferredCurrency || "$ CAD");

      const configDoc = await getDoc(
        doc(getFirestore(), "configuration", "configV2"),
      );

      //Filtering this way to follow the warehoue ordering in the warehouses section of the config doc
      const warehousesInRegion = configDoc
        .data()
        .warehouses.filter((warehouse) => {
          return configDoc
            .data()
            .regions[region].includes(warehouse.letterCode);
        });
      setWarehouses(warehousesInRegion);

      setCurrencyOptions(configDoc.data().currencyOptions);
    })();
  }, [authContextLoaded, tooManyDevices, user]);

  const changePreferredCurrency = async (newCurrency) => {
    setPreferredCurrency(newCurrency);
    await updateDoc(doc(getFirestore(), "users", user.uid), {
      preferredCurrency: newCurrency,
    });
  };

  //Share variables and methods to children through the provider
  return (
    <DatabaseReaderContext.Provider
      value={{
        warehouses,
        userRegion,
        currencyOptions,
        preferredCurrency,
        changePreferredCurrency,
      }}
    >
      {children}
      {/* <Outlet /> */}
    </DatabaseReaderContext.Provider>
  );
};

export default DatabaseReaderContextProvider;

//CREATE A HOOK TO BE USED ON A CONSUMER COMPONENT TO READ AUTH VARIABLES AND METHODS
export const useDatabaseReader = () => useContext(DatabaseReaderContext);
// Enhanced hook for inventory freshness monitoring with data parser completion detection
export const useInventoryFreshness = (onFreshData = null, options = {}, productData = null) => {
  const { 
    staleThresholdMinutes = 34,
    enableTimeBasedRefresh = false, // Default to completion-based only
    enableCompletionBasedRefresh = true 
  } = options;
  
  const { user } = useAuth(); // Get authentication state
  const isAuthenticated = !!user;
  
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const lastDataVersionRef = useRef(null);
  const hasTriggeredReloadRef = useRef(false);
  const onFreshDataRef = useRef(onFreshData);
  const timeBasedIntervalRef = useRef(null);
  const lastProcessedTimestampRef = useRef(null);
  const refreshAttemptCountRef = useRef(0);
  
  // Update ref when callback changes
  useEffect(() => {
    onFreshDataRef.current = onFreshData;
  }, [onFreshData]);
  
  useEffect(() => {
    // For unauthenticated users (public links), use product timestamp-based fallback
    if (!isAuthenticated) {
      console.log("Public user detected - using product timestamp-based inventory freshness fallback");
      
      // Clear any existing timeout
      if (timeBasedIntervalRef.current) {
        clearTimeout(timeBasedIntervalRef.current);
      }
      
      // Only proceed if we have product data with timestamps
      if (productData && productData.length > 0) {
        // Extract all updatedAt timestamps from products
        const allTimestamps = productData.flatMap(article => 
          article.colors.map(color => color.updatedAt)
        ).filter(timestamp => timestamp && timestamp > 0);
        
        if (allTimestamps.length > 0) {
          // Find oldest timestamp (most stale data)
          const oldestTimestamp = Math.min(...allTimestamps);
          const ageMs = Date.now() - oldestTimestamp;
          const staleThresholdMs = staleThresholdMinutes * 60 * 1000;
          
          console.log(`Public user: Oldest product is ${Math.floor(ageMs / (1000 * 60))} minutes old`);
          
          // Check if this is the same data we've already processed to prevent infinite loops
          const isSameData = lastProcessedTimestampRef.current === oldestTimestamp;
          
          if (ageMs >= staleThresholdMs) {
            // Data is already stale
            if (!hasTriggeredReloadRef.current && onFreshDataRef.current && !isSameData) {
              console.log(`Public user: Data is already stale (${Math.floor(ageMs / (1000 * 60))}+ minutes old) - triggering immediate refresh`);
              
              // Track this timestamp to prevent re-triggering for same data
              lastProcessedTimestampRef.current = oldestTimestamp;
              refreshAttemptCountRef.current += 1;
              
              hasTriggeredReloadRef.current = true;
              onFreshDataRef.current();
              
              // Use exponential backoff if multiple attempts with same data
              const backoffDelay = Math.min(5000 * Math.pow(2, refreshAttemptCountRef.current - 1), 30000); // Max 30 seconds
              setTimeout(() => {
                hasTriggeredReloadRef.current = false;
              }, backoffDelay);
              
              console.log(`Public user: Next retry allowed in ${backoffDelay / 1000} seconds if data doesn't update`);
            } else if (isSameData) {
              console.log(`Public user: Skipping refresh - already processed this stale data (attempt ${refreshAttemptCountRef.current})`);
            }
          } else {
            // Data is fresh, reset counters and schedule refresh for when it becomes stale
            lastProcessedTimestampRef.current = null;
            refreshAttemptCountRef.current = 0;
            
            const timeUntilStale = staleThresholdMs - ageMs;
            console.log(`Public user: Scheduling refresh in ${Math.floor(timeUntilStale / (1000 * 60))} minutes when data becomes ${staleThresholdMinutes}+ minutes old`);
            
            timeBasedIntervalRef.current = setTimeout(() => {
              if (!hasTriggeredReloadRef.current && onFreshDataRef.current) {
                console.log(`Public user: Auto-reloading inventory - data is now ${staleThresholdMinutes}+ minutes old`);
                lastProcessedTimestampRef.current = oldestTimestamp;
                refreshAttemptCountRef.current = 1;
                hasTriggeredReloadRef.current = true;
                onFreshDataRef.current();
                setTimeout(() => {
                  hasTriggeredReloadRef.current = false;
                }, 5000);
              }
            }, timeUntilStale);
          }
        } else {
          console.log("Public user: No valid product timestamps found, skipping auto-refresh scheduling");
        }
      } else {
        console.log("Public user: No product data available, skipping auto-refresh scheduling");
      }
      
      return () => {
        if (timeBasedIntervalRef.current) {
          clearTimeout(timeBasedIntervalRef.current);
        }
      };
    }
    
    // For authenticated users, use the existing Firestore listeners
    const db = getFirestore();
    
    // Wrap Firestore listeners in try-catch for graceful error handling
    let statsUnsubscribe;
    let executionsUnsubscribe;
    
    try {
      // Listen to stats updates to track inventory freshness
      statsUnsubscribe = onSnapshot(
      doc(db, "dataParser", "config", "stats", "main"),
      (snapshot) => {
        if (snapshot.exists()) {
          const stats = snapshot.data();
          const lastUpdatedTime = stats.lastUpdated;
          
          if (lastUpdatedTime) {
            const lastUpdate = new Date(lastUpdatedTime._seconds ? lastUpdatedTime._seconds * 1000 : lastUpdatedTime);
            setLastUpdated(lastUpdate);
            
            // Completion-based refresh: Check if we have fresh data (new completion)
            if (enableCompletionBasedRefresh && stats.lastStatus === 'success') {
              const currentDataVersion = `${lastUpdate.getTime()}-${stats.lastStatus}`;
              
              if (lastDataVersionRef.current && lastDataVersionRef.current !== currentDataVersion && !hasTriggeredReloadRef.current) {
                console.log(`Fresh inventory data detected - triggering refresh (completion-based)`);
                hasTriggeredReloadRef.current = true;
                if (onFreshDataRef.current) {
                  onFreshDataRef.current();
                }
                // Reset the trigger after a delay to allow for future updates
                setTimeout(() => {
                  hasTriggeredReloadRef.current = false;
                }, 5000); // 5 second cooldown
              }
              lastDataVersionRef.current = currentDataVersion;
            }
            
            // Time-based refresh: Check if data is older than threshold
            if (enableTimeBasedRefresh) {
              const thresholdTime = new Date(Date.now() - staleThresholdMinutes * 60 * 1000);
              const dataIsStale = lastUpdate < thresholdTime;
              
              setIsStale(dataIsStale);
              
              // Auto-trigger reload if data is stale and we haven't already triggered it
              if (dataIsStale && !hasTriggeredReloadRef.current && onFreshData) {
                console.log(`Auto-reloading inventory - data is ${Math.floor((Date.now() - lastUpdate.getTime()) / (1000 * 60))} minutes old`);
                hasTriggeredReloadRef.current = true;
                onFreshData();
                // Reset after triggering
                setTimeout(() => {
                  hasTriggeredReloadRef.current = false;
                }, 5000);
              }
            }
          }
        }
      },
      (error) => {
        console.error("Error listening to inventory freshness:", error);
      }
    );

      // Listen to execution history for real-time completion detection
      if (enableCompletionBasedRefresh) {
        const executionsQuery = query(
          collection(db, "dataParser", "history", "executions"),
          orderBy("startTime", "desc"),
          limit(1)
        );

        let lastExecutionIdRef = null;
        
        executionsUnsubscribe = onSnapshot(
          executionsQuery,
          (snapshot) => {
            if (!snapshot.empty) {
              const latestExecution = snapshot.docs[0].data();
              const executionId = snapshot.docs[0].id;
              
              // Check if a successful execution just completed
              if (latestExecution.status === 'success' && latestExecution.endTime) {
                // Check if this is a new execution we haven't seen before
                if (lastExecutionIdRef && lastExecutionIdRef !== executionId && !hasTriggeredReloadRef.current) {
                  console.log(`Data parser completed successfully - triggering refresh (execution-based)`);
                  hasTriggeredReloadRef.current = true;
                  if (onFreshDataRef.current) {
                    onFreshDataRef.current();
                  }
                  // Reset after triggering
                  setTimeout(() => {
                    hasTriggeredReloadRef.current = false;
                  }, 5000);
                }
                lastExecutionIdRef = executionId;
              }
            }
          },
          (error) => {
            console.error("Error listening to execution updates:", error);
          }
        );
      }
    } catch (error) {
      console.error("Error setting up inventory freshness listeners:", error);
      // Fallback: disable real-time updates for this session
    }

    return () => {
      if (timeBasedIntervalRef.current) {
        clearInterval(timeBasedIntervalRef.current);
      }
      if (statsUnsubscribe) {
        statsUnsubscribe();
      }
      if (executionsUnsubscribe) {
        executionsUnsubscribe();
      }
    };
  }, [staleThresholdMinutes, enableTimeBasedRefresh, enableCompletionBasedRefresh, isAuthenticated, productData]); // Added productData to deps

  const forceRefresh = () => {
    hasTriggeredReloadRef.current = false;
    if (onFreshDataRef.current) {
      onFreshDataRef.current();
    }
  };

  const resetReloadTrigger = () => {
    hasTriggeredReloadRef.current = false;
  };

  return { 
    lastUpdated, 
    isStale, 
    hasTriggeredReload: hasTriggeredReloadRef.current,
    forceRefresh,
    resetReloadTrigger,
    minutesSinceUpdate: lastUpdated ? Math.floor((Date.now() - lastUpdated.getTime()) / (1000 * 60)) : null
  };
};
